import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

interface Campaign {
  id: string;
  name: string;
  subject: string;
  sender_type: string;
  recipient_group: string;
  status: string;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  total_recipients: number;
  sent_count: number;
  opened_count: number;
  clicked_count: number;
  bounced_count: number;
  open_rate: number;
  click_rate: number;
}

interface CampaignListProps {
  onSelect: (id: string) => void;
  onCompose: () => void;
}

const STATUS_BADGES: Record<string, { bg: string; color: string }> = {
  draft: { bg: '#f0f0f0', color: '#666' },
  scheduled: { bg: '#e3f2fd', color: '#1565c0' },
  sending: { bg: '#fff3e0', color: '#e65100' },
  sent: { bg: '#e8f5e9', color: '#2e7d32' },
  cancelled: { bg: '#fce4ec', color: '#c62828' },
};

const SENDER_LABELS: Record<string, string> = {
  brad: 'Brad',
  noreply: 'No-Reply',
  office: 'Office',
};

const GROUP_LABELS: Record<string, string> = {
  all: 'All Customers',
  wholesale: 'Wholesale',
  retail_partners: 'Retail',
  bridge_club: 'Bridge Club',
  farmhand: 'Farmhand',
  custom: 'Custom',
};

const CampaignList: React.FC<CampaignListProps> = ({ onSelect, onCompose }) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadCampaigns();
  }, [statusFilter]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      const res = await api.get('/admin/campaigns/', { params });
      setCampaigns(res.data);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['', 'draft', 'scheduled', 'sent', 'cancelled'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                padding: '0.4rem 0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: statusFilter === s ? '#2D5A27' : 'white',
                color: statusFilter === s ? 'white' : '#333',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
        <button
          onClick={onCompose}
          style={{
            padding: '0.5rem 1rem',
            background: '#2D5A27',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          + New Campaign
        </button>
      </div>

      {loading ? (
        <p style={{ color: '#999', textAlign: 'center', padding: '2rem' }}>Loading campaigns...</p>
      ) : campaigns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#999' }}>
          <p>No campaigns yet. Create your first email campaign!</p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
              <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>Campaign</th>
              <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>Audience</th>
              <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>Recipients</th>
              <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>Opens</th>
              <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>Clicks</th>
              <th style={{ padding: '0.75rem 0.5rem', fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map(c => {
              const badge = STATUS_BADGES[c.status] || STATUS_BADGES.draft;
              return (
                <tr
                  key={c.id}
                  onClick={() => onSelect(c.id)}
                  style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                  onMouseOver={e => (e.currentTarget.style.background = '#fafafa')}
                  onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <div style={{ fontWeight: 500 }}>{c.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#999' }}>{c.subject}</div>
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <span style={{
                      padding: '3px 8px',
                      borderRadius: '10px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      backgroundColor: badge.bg,
                      color: badge.color,
                    }}>
                      {c.status}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.85rem', color: '#666' }}>
                    {GROUP_LABELS[c.recipient_group] || c.recipient_group}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.9rem' }}>
                    {c.total_recipients > 0 ? c.total_recipients : '-'}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.9rem' }}>
                    {c.total_recipients > 0 ? `${c.open_rate}%` : '-'}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.9rem' }}>
                    {c.total_recipients > 0 ? `${c.click_rate}%` : '-'}
                  </td>
                  <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.85rem', color: '#666' }}>
                    {c.sent_at
                      ? new Date(c.sent_at).toLocaleDateString()
                      : c.scheduled_at
                      ? `Sched: ${new Date(c.scheduled_at).toLocaleDateString()}`
                      : new Date(c.created_at).toLocaleDateString()
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CampaignList;
